/**
 * ENG-5641 / ENG-6396 — real MCP connectivity probe client (streamable-HTTP).
 *
 * Does a genuine MCP handshake against a remote streamable-HTTP MCP server —
 * initialize → notifications/initialized → tools/list — and maps the result to
 * a {@link ConnectivityProbeOutcome}. `tools/list` is the cheapest read that
 * proves the server is reachable AND speaking MCP (not just that a TCP port is
 * open). Framing mirrors composio-tool-call-probe.ts.
 *
 * Streamable-HTTP only (remote-MCP providers like granola, and HTTP proxies).
 * Stdio MCP servers (spawned from the agent's .mcp.json `command`) are a
 * follow-up — the manager would spawn the process with the agent's env and
 * speak JSON-RPC over stdio.
 *
 * Lives in core (not apps/cli) so BOTH callers exercise one implementation:
 *   - the manager CLI's host-side connectivity-probe runner, and
 *   - the API's synchronous `POST /integrations/:id/test` path (ENG-6396),
 *     which previously fell through to a "Credentials present" non-verification
 *     for remote-MCP OAuth providers and so never surfaced an expired token.
 *
 * `fetch` is injected so the handshake + status mapping is unit-testable.
 * Read-only: initialize + a tools listing. ENG-6957 adds an OPTIONAL real
 * `tools/call` when the toolkit defines a `connectivity_test` tool — a
 * read-only call (e.g. Kajabi `list_sites`) that proves the integration can
 * actually EXECUTE, not just that the server answers `tools/list` (which an
 * expired/degraded token can still pass). Without a `connectivity_test` the
 * probe stays tools/list-only, unchanged.
 *
 * Status mapping:
 *   - 'ok'              tools/list (and the connectivity_test tools/call, when
 *                       configured) returned a result
 *   - 'down'           401/403 (auth), other non-429 4xx, a JSON-RPC error, or a
 *                       tool-level `isError` result from the server
 *   - 'transient_error' 429 / 5xx / network / timeout — retryable
 *
 * ENG-10574: a non-ok HTTP response is graded by the SAME `statusForHttp` /
 * `causeForHttp` pair the central HTTP lane uses, rather than a local copy.
 * The local copy had already drifted: it graded a 429 `down`, which runs the
 * full hard-down ladder (and can end in `needs_reauth`, telling a customer to
 * reconnect a credential that never stopped working) - the exact shape ENG-5463
 * removed from the central lane. See `httpStatusOutcome`.
 */

import type { ConnectivityProbeOutcome, ConnectivityTestOverride } from './connectivity-probe.js';
import { handshakeToolsListOutcome } from './connectivity-probe.js';
import { causeForHttp, statusForHttp } from './connectivity-http-probes.js';

const MCP_ACCEPT = 'application/json, text/event-stream';
const DEFAULT_TIMEOUT_MS = 10_000;

export interface McpHttpProbeConfig {
  url: string;
  headers?: Record<string, string>;
  timeoutMs?: number;
  /**
   * ENG-6957 — optional toolkit `connectivity_test`. When its `tool` is set,
   * the probe issues a real read-only `tools/call` to that tool after a
   * successful `tools/list` and folds the result into the verdict. `args`
   * (object form) is passed as the call `arguments`; a string[] (cli_tool form)
   * is ignored here. Absent ⇒ tools/list-only (unchanged).
   */
  connectivityTest?: ConnectivityTestOverride | null;
}

/**
 * True iff `msg` is a bona-fide JSON-RPC response envelope for `expectedId`:
 * an object carrying a `result` OR `error` member whose `id` matches the
 * request. ENG-6396 review hardening — without this gate the probe would treat
 * any 200-with-JSON body (a non-MCP endpoint, an OAuth error page rendered as
 * JSON, a proxy health blob) as a reachable MCP server, reintroducing the very
 * false-green this PR removes.
 */
function isRpcEnvelopeFor(msg: unknown, expectedId: number): msg is Record<string, unknown> {
  return (
    typeof msg === 'object' &&
    msg !== null &&
    ('result' in msg || 'error' in msg) &&
    (msg as Record<string, unknown>)['id'] === expectedId
  );
}

/**
 * Extract the JSON-RPC response for `expectedId` from a JSON or SSE
 * (text/event-stream) body. Returns the envelope only when it is a valid
 * JSON-RPC `result`/`error` for that id; otherwise `null` (the caller maps null
 * to a `down` — the server answered but is not speaking MCP).
 */
async function parseRpc(res: Response, expectedId: number): Promise<Record<string, unknown> | null> {
  const ct = res.headers.get('content-type') ?? '';
  if (ct.includes('text/event-stream')) {
    const text = await res.text();
    let dataLines: string[] = [];
    const tryFrame = (): Record<string, unknown> | null => {
      if (dataLines.length === 0) return null;
      try {
        const msg = JSON.parse(dataLines.join('\n')) as unknown;
        if (isRpcEnvelopeFor(msg, expectedId)) return msg;
      } catch { /* skip malformed frame */ }
      return null;
    };
    for (const rawLine of text.split(/\r?\n/)) {
      if (rawLine.startsWith('data:')) {
        dataLines.push(rawLine.slice(5).trimStart());
        continue;
      }
      if (rawLine === '') {
        const frame = tryFrame();
        if (frame) return frame;
        dataLines = [];
      }
    }
    // A final frame not terminated by a trailing blank line (some servers omit it).
    return tryFrame();
  }
  const msg = (await res.json().catch(() => null)) as unknown;
  return isRpcEnvelopeFor(msg, expectedId) ? msg : null;
}

/**
 * Grade a non-ok HTTP response from any step of the MCP handshake.
 *
 * ENG-10574. Status and cause come from the central lane's pair, so the two
 * lanes cannot disagree about the same response. Three decisions worth keeping:
 *
 * 1. **A 429 is `transient_error` + `rate_limited`, no longer `down`.** A
 *    behaviour change, and the intended one: RFC 6585 gives 429 exactly one
 *    meaning, so it needs no corroboration, and grading it `down` ran the whole
 *    hard-down ladder against a credential that was fine. `rate_limited` is
 *    vendor-side, so a long throttle streak also stops being promoted to a
 *    reconnect page (`isTransientStreakPermanent`, ENG-9173).
 *
 * 2. **A 403 is NOT disambiguated by response headers here.** The central lane
 *    reads `retry-after` / `x-ratelimit-remaining` to downgrade a throttling
 *    403, but only for providers whose 403 is known to be ambiguous - it is
 *    deliberately opt-in, because Vercel and Higgsfield 403 a dead credential and
 *    a header-driven downgrade would read that as retryable and never escalate
 *    it. This arm fronts arbitrary remote MCP servers whose 403 semantics nobody
 *    has characterised, so it takes the file's own default: "a false `down` is
 *    loud and wrong; a false `transient_error` is quiet and wrong, and quiet is
 *    worse". Opting in belongs on a per-server basis once one is characterised.
 *
 * 3. **401/403 is `auth_rejected`, not `access_restricted`.** Both callers send
 *    the INTEGRATION'S credential as the bearer: the API route passes the
 *    customer's OAuth `access_token` (`integrations.ts`), and the host lane
 *    passes the headers from the agent's own `.mcp.json`. Neither is a platform
 *    key, so a refusal is the customer's grant being refused - the case
 *    `auth_rejected` names, whose remedy is the reconnect this message already
 *    tells the operator to do. `access_restricted` means OUR key was refused and
 *    "pairs with `unverified`, never `down`", so it would contradict the status.
 *
 * Other non-429 4xx land on `semantic`, inherited from `causeForHttp` ("the
 * request was authenticated and reached the provider, and the provider rejected
 * the ASK"). A 404 on an MCP URL is the common case: reachable, wrong endpoint.
 */
function httpStatusOutcome(status: number, step: string): ConnectivityProbeOutcome {
  const graded = statusForHttp(status);
  const cause = causeForHttp(status);
  if (status === 401 || status === 403) {
    return { status: graded, cause, message: `MCP ${step} unauthorized (${status}) — reconnect required` };
  }
  if (status === 429) {
    return {
      status: graded,
      cause,
      message: `MCP ${step} rate limited (${status}) — not a credential failure`,
    };
  }
  return { status: graded, cause, message: `MCP ${step} returned ${status}` };
}

/**
 * Probe a streamable-HTTP MCP server. Returns the connectivity outcome.
 */
export async function probeMcpHttp(
  config: McpHttpProbeConfig,
  fetchImpl: typeof fetch = fetch,
): Promise<ConnectivityProbeOutcome> {
  const timeoutMs = config.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const baseHeaders: Record<string, string> = {
    ...(config.headers ?? {}),
    'Content-Type': 'application/json',
    Accept: MCP_ACCEPT,
  };

  try {
    // 1. initialize
    const initRes = await fetchImpl(config.url, {
      method: 'POST',
      headers: baseHeaders,
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: 1,
        method: 'initialize',
        params: {
          protocolVersion: '2025-03-26',
          capabilities: {},
          clientInfo: { name: 'augmented-connectivity-probe', version: '1.0.0' },
        },
      }),
      signal: AbortSignal.timeout(timeoutMs),
    });
    if (!initRes.ok) return httpStatusOutcome(initRes.status, 'initialize');

    const sessionId = initRes.headers.get('mcp-session-id');
    const initRpc = await parseRpc(initRes, 1);
    if (!initRpc) {
      return { status: 'down', message: 'MCP initialize returned a non-JSON-RPC response — not an MCP server' };
    }
    if ('error' in initRpc) {
      const err = initRpc['error'] as { message?: string } | undefined;
      return { status: 'down', message: `MCP initialize error: ${err?.message ?? 'unknown'}` };
    }
    const sessionHeaders = { ...baseHeaders, ...(sessionId ? { 'Mcp-Session-Id': sessionId } : {}) };

    // 2. notifications/initialized
    const initializedRes = await fetchImpl(config.url, {
      method: 'POST',
      headers: sessionHeaders,
      body: JSON.stringify({ jsonrpc: '2.0', method: 'notifications/initialized' }),
      signal: AbortSignal.timeout(5_000),
    });
    if (!initializedRes.ok) return httpStatusOutcome(initializedRes.status, 'initialized');
    await initializedRes.text().catch(() => '');

    // 3. tools/list — the cheap read that proves MCP reachability.
    const listRes = await fetchImpl(config.url, {
      method: 'POST',
      headers: sessionHeaders,
      body: JSON.stringify({ jsonrpc: '2.0', id: 2, method: 'tools/list' }),
      signal: AbortSignal.timeout(timeoutMs),
    });
    if (!listRes.ok) return httpStatusOutcome(listRes.status, 'tools/list');

    const rpc = await parseRpc(listRes, 2);
    if (!rpc) {
      return { status: 'down', message: 'MCP tools/list returned a non-JSON-RPC response — not an MCP server' };
    }
    if ('error' in rpc) {
      const err = rpc['error'] as { message?: string } | undefined;
      return { status: 'down', message: `MCP tools/list error: ${err?.message ?? 'unknown'}` };
    }
    const result = rpc['result'] as { tools?: unknown[] } | undefined;
    const toolCount = Array.isArray(result?.tools) ? result!.tools!.length : undefined;

    // 4. ENG-6957 — optional read-only tools/call to the toolkit's
    //    connectivity_test tool. Proves the integration can EXECUTE, not just
    //    that tools/list answers. Skipped entirely when no test tool is set.
    const testTool = config.connectivityTest?.tool;
    if (testTool) {
      const rawArgs = config.connectivityTest?.args;
      // managed/MCP form is an object; a string[] (cli_tool form) doesn't apply
      // to a tools/call and is treated as no args.
      const toolArgs = rawArgs && !Array.isArray(rawArgs) ? rawArgs : {};
      const callRes = await fetchImpl(config.url, {
        method: 'POST',
        headers: sessionHeaders,
        body: JSON.stringify({
          jsonrpc: '2.0',
          id: 3,
          method: 'tools/call',
          params: { name: testTool, arguments: toolArgs },
        }),
        signal: AbortSignal.timeout(timeoutMs),
      });
      if (!callRes.ok) return httpStatusOutcome(callRes.status, `tools/call ${testTool}`);

      const callRpc = await parseRpc(callRes, 3);
      if (!callRpc) {
        return { status: 'down', message: `MCP tools/call ${testTool} returned a non-JSON-RPC response — not an MCP server` };
      }
      if ('error' in callRpc) {
        const err = callRpc['error'] as { message?: string } | undefined;
        return { status: 'down', message: `MCP tools/call ${testTool} error: ${err?.message ?? 'unknown'}` };
      }
      // MCP tool-level failure: the JSON-RPC call succeeded but the tool
      // reported an error via `result.isError`. That's a real "can't execute"
      // signal, not a green.
      const callResult = callRpc['result'] as { isError?: boolean } | undefined;
      if (callResult?.isError === true) {
        return { status: 'down', message: `MCP tool ${testTool} returned an error result` };
      }
      return {
        status: 'ok',
        message: `${testTool} succeeded`,
        details: { ...(toolCount !== undefined ? { toolCount } : {}), testTool },
      };
    }

    // ENG-8358: a zero-tool manifest is NOT a green (see handshakeToolsListOutcome).
    return handshakeToolsListOutcome(toolCount);
  } catch (err) {
    const isAbort = (err as Error)?.name === 'TimeoutError' || (err as Error)?.name === 'AbortError';
    return {
      status: 'transient_error',
      // ENG-10574: a timeout, DNS failure or refused connection - "network
      // failure, DNS, or our own timeout". Same call `networkOutcome` makes for
      // the central lane. Not vendor-side, so escalation is unchanged.
      cause: 'unreachable',
      message: isAbort ? `MCP handshake timed out after ${timeoutMs / 1000}s` : `MCP handshake failed: ${(err as Error).message}`,
    };
  }
}
